// Client helpers for the shared Proposal (draft-and-confirm) primitive, reached cross-app in the
// Developer (t2) app like the assistant itself: t2/v1/developer_proposals, PUT {USID, Action,
// DecisionNote} decides one. A QMS write the assistant drafts is a proposal whose payload is the
// exact service call the screen would have made; approving it replays that call under the
// approver's persona. The QMS app has no proposals inbox; the card in the assistant drawer is the
// only place a proposal is decided, which is also the only place it is shown.
import AxiosDeveloperAPI from "../util/AxiosDeveloperAPI";
import {referencePath} from "./assistant";

export type ProposalDecision = {
    /** "approved" | "rejected" (or the prior status if it was already decided). */
    status: string;
    /** USID of the record created on approval: a revision for a record, the change order's own USID. */
    targetUSID?: string;
    proposalType?: string;
    /** What the target is, so the drawer can open it: "Record" | "ChangeOrder". */
    open?: string;
    /** True if the proposal had already been decided before this call. */
    alreadyDecided?: boolean;
};

async function decide(usid: string, action: "approve" | "reject", note?: string): Promise<ProposalDecision> {
    const res = await AxiosDeveloperAPI({
        method: "PUT",
        url: "t2/v1/developer_proposals",
        data: {USID: usid, Action: action, DecisionNote: note},
    });
    const body = res?.data ?? {};
    return {
        status: String(body.status ?? `${action}ed`),
        targetUSID: body.targetUSID ? String(body.targetUSID) : undefined,
        proposalType: body.proposalType ? String(body.proposalType) : undefined,
        open: body.open ? String(body.open) : undefined,
        alreadyDecided: !!body.alreadyDecided,
    };
}

export function approveProposal(usid: string, note?: string): Promise<ProposalDecision> {
    return decide(usid, "approve", note);
}

export function rejectProposal(usid: string, note?: string): Promise<ProposalDecision> {
    return decide(usid, "reject", note);
}

/** The in-app route to what an approved proposal made, or "" when nothing opens. */
export function targetLink(decision: ProposalDecision, fallbackOpen?: string): string {
    if (!decision.targetUSID) return "";
    const kind = decision.open || fallbackOpen || "Record";
    return referencePath(kind, decision.targetUSID);
}
